import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Credit, CreditCreateDto, CreditBalance, CreditsResponse, CreditFilters, CreditSort } from "../../modules/shared/model";
import { 
	createCredit as createCreditAction, 
	updateCredit as updateCreditAction, 
	getCreditById as getCreditByIdAction, 
	getCredits as getCreditsAction, 
	deleteCredit as deleteCreditAction,
	getUserCredits as getUserCreditsAction,
	getUserCreditBalance as getUserCreditBalanceAction,
	getCreditsWithFilters as getCreditsWithFiltersAction,
	getUserCreditsWithFilters as getUserCreditsWithFiltersAction
} from "../Actions/CreditActions";

export const createCredit = createAsyncThunk("credits/create", async (data: CreditCreateDto, { rejectWithValue }) => {
	try { return await createCreditAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create credit"); }
});

export const updateCredit = createAsyncThunk("credits/update", async ({ id, data }: { id: string; data: Partial<CreditCreateDto> }, { rejectWithValue }) => {
	try { return await updateCreditAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update credit"); }
});

export const fetchCredit = createAsyncThunk("credits/getById", async (id: string, { rejectWithValue }) => {
	try { return await getCreditByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch credit"); }
});

export const fetchCredits = createAsyncThunk("credits/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { return await getCreditsAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch credits"); }
});

export const deleteCredit = createAsyncThunk("credits/delete", async (id: string, { rejectWithValue }) => {
	try { await deleteCreditAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete credit"); }
});

export const fetchUserCredits = createAsyncThunk("credits/getUserCredits", async ({ userId, query }: { userId: string; query?: Record<string, string | number | boolean> }, { rejectWithValue }) => {
	try { return await getUserCreditsAction(userId, query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch user credits"); }
});

export const fetchUserCreditBalance = createAsyncThunk("credits/getUserBalance", async (userId: string, { rejectWithValue }) => {
	try { return await getUserCreditBalanceAction(userId); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch user credit balance"); }
});

export const fetchCreditsWithFilters = createAsyncThunk("credits/getWithFilters", async ({ filters, sort, page, limit }: { filters?: CreditFilters; sort?: CreditSort[]; page?: number; limit?: number }, { rejectWithValue }) => {
	try { return await getCreditsWithFiltersAction(filters, sort, page, limit); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch credits with filters"); }
});

export const fetchUserCreditsWithFilters = createAsyncThunk("credits/getUserCreditsWithFilters", async ({ userId, filters, sort, page, limit }: { userId: string; filters?: CreditFilters; sort?: CreditSort[]; page?: number; limit?: number }, { rejectWithValue }) => {
	try { return await getUserCreditsWithFiltersAction(userId, filters, sort, page, limit); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch user credits with filters"); }
});

interface CreditsState {
	items: Credit[];
	current: Credit | null;
	loading: boolean;
	error: string | null;
	totalCount: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	page: number;
	limit: number;
	totalPages: number;
	// User-specific credits
	userCredits: Credit[];
	userCreditsLoading: boolean;
	userCreditsError: string | null;
	userCreditsTotalCount: number;
	userCreditsHasNextPage: boolean;
	userCreditsHasPreviousPage: boolean;
	userCreditsPage: number;
	userCreditsLimit: number;
	userCreditsTotalPages: number;
	// User balance
	userBalance: CreditBalance | null;
	userBalanceLoading: boolean;
	userBalanceError: string | null;
}

const initialState: CreditsState = {
	items: [],
	current: null,
	loading: false,
	error: null,
	totalCount: 0,
	hasNextPage: false,
	hasPreviousPage: false,
	page: 1,
	limit: 10,
	totalPages: 0,
	// User-specific credits
	userCredits: [],
	userCreditsLoading: false,
	userCreditsError: null,
	userCreditsTotalCount: 0,
	userCreditsHasNextPage: false,
	userCreditsHasPreviousPage: false,
	userCreditsPage: 1,
	userCreditsLimit: 10,
	userCreditsTotalPages: 0,
	// User balance
	userBalance: null,
	userBalanceLoading: false,
	userBalanceError: null,
};

const creditsSlice = createSlice({
	name: "credits",
	initialState,
	reducers: {
		clearCreditsError(state) { state.error = null; },
		clearCurrentCredit(state) { state.current = null; },
		clearUserCreditsError(state) { state.userCreditsError = null; },
		clearUserBalanceError(state) { state.userBalanceError = null; },
		clearUserCredits(state) {
			state.userCredits = [];
			state.userCreditsTotalCount = 0;
			state.userCreditsHasNextPage = false;
			state.userCreditsHasPreviousPage = false;
			state.userCreditsPage = 1;
			state.userCreditsTotalPages = 0;
		},
		clearUserBalance(state) { state.userBalance = null; },
		clearAllCredits(state) {
			state.items = [];
			state.current = null;
			state.totalCount = 0;
			state.hasNextPage = false;
			state.hasPreviousPage = false;
			state.page = 1;
			state.totalPages = 0;
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(createCredit.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(createCredit.fulfilled, (s, a: PayloadAction<Credit>) => { s.loading = false; s.items.unshift(a.payload); s.totalCount = s.totalCount + 1; })
			.addCase(createCredit.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(updateCredit.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(updateCredit.fulfilled, (s, a: PayloadAction<Credit>) => {
				s.loading = false;
				const i = s.items.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.items[i] = a.payload;
				if (s.current?.id === a.payload.id) s.current = a.payload;
			})
			.addCase(updateCredit.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(fetchCredit.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchCredit.fulfilled, (s, a: PayloadAction<Credit>) => { s.loading = false; s.current = a.payload; })
			.addCase(fetchCredit.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(fetchCredits.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchCredits.fulfilled, (s, a: PayloadAction<CreditsResponse>) => { 
				s.loading = false; 
				s.items = a.payload.data || []; 
				s.totalCount = a.payload.totalCount || 0;
				s.hasNextPage = a.payload.hasNextPage || false;
				s.hasPreviousPage = a.payload.hasPreviousPage || false;
				s.page = a.payload.page || 1;
				s.limit = a.payload.limit || 10;
				s.totalPages = a.payload.totalPages || 0;
			})
			.addCase(fetchCredits.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(deleteCredit.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(deleteCredit.fulfilled, (s, a: PayloadAction<string>) => { 
				s.loading = false; 
				s.items = s.items.filter(x => x.id !== a.payload); 
				if (s.current?.id === a.payload) s.current = null; 
				s.totalCount = Math.max(0, s.totalCount - 1); 
			})
			.addCase(deleteCredit.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		// User Credits
		builder
			.addCase(fetchUserCredits.pending, (s) => { s.userCreditsLoading = true; s.userCreditsError = null; })
			.addCase(fetchUserCredits.fulfilled, (s, a: PayloadAction<CreditsResponse>) => { 
				s.userCreditsLoading = false; 
				s.userCredits = a.payload.data || []; 
				s.userCreditsTotalCount = a.payload.totalCount || 0;
				s.userCreditsHasNextPage = a.payload.hasNextPage || false;
				s.userCreditsHasPreviousPage = a.payload.hasPreviousPage || false;
				s.userCreditsPage = a.payload.page || 1;
				s.userCreditsLimit = a.payload.limit || 10;
				s.userCreditsTotalPages = a.payload.totalPages || 0;
			})
			.addCase(fetchUserCredits.rejected, (s, a: any) => { s.userCreditsLoading = false; s.userCreditsError = a.payload as string; });

		// User Balance
		builder
			.addCase(fetchUserCreditBalance.pending, (s) => { s.userBalanceLoading = true; s.userBalanceError = null; })
			.addCase(fetchUserCreditBalance.fulfilled, (s, a: PayloadAction<CreditBalance>) => { s.userBalanceLoading = false; s.userBalance = a.payload; })
			.addCase(fetchUserCreditBalance.rejected, (s, a: any) => { s.userBalanceLoading = false; s.userBalanceError = a.payload as string; });

		// Credits with Filters
		builder
			.addCase(fetchCreditsWithFilters.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchCreditsWithFilters.fulfilled, (s, a: PayloadAction<CreditsResponse>) => { 
				s.loading = false; 
				s.items = a.payload.data || []; 
				s.totalCount = a.payload.totalCount || 0;
				s.hasNextPage = a.payload.hasNextPage || false;
				s.hasPreviousPage = a.payload.hasPreviousPage || false;
				s.page = a.payload.page || 1;
				s.limit = a.payload.limit || 10;
				s.totalPages = a.payload.totalPages || 0;
			})
			.addCase(fetchCreditsWithFilters.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		// User Credits with Filters
		builder
			.addCase(fetchUserCreditsWithFilters.pending, (s) => { s.userCreditsLoading = true; s.userCreditsError = null; })
			.addCase(fetchUserCreditsWithFilters.fulfilled, (s, a: PayloadAction<CreditsResponse>) => { 
				s.userCreditsLoading = false; 
				s.userCredits = a.payload.data || []; 
				s.userCreditsTotalCount = a.payload.totalCount || 0;
				s.userCreditsHasNextPage = a.payload.hasNextPage || false;
				s.userCreditsHasPreviousPage = a.payload.hasPreviousPage || false;
				s.userCreditsPage = a.payload.page || 1;
				s.userCreditsLimit = a.payload.limit || 10;
				s.userCreditsTotalPages = a.payload.totalPages || 0;
			})
			.addCase(fetchUserCreditsWithFilters.rejected, (s, a: any) => { s.userCreditsLoading = false; s.userCreditsError = a.payload as string; });
	}
});

export const { 
	clearCreditsError, 
	clearCurrentCredit, 
	clearUserCreditsError,
	clearUserBalanceError,
	clearUserCredits,
	clearUserBalance,
	clearAllCredits
} = creditsSlice.actions;
export default creditsSlice.reducer;
